import type { Customer } from '@clockwise/clients/customers';
import type { Department } from '@clockwise/clients/departments';
import type {
  ReportColumnMeta,
  ReportSortField,
  TimesheetReportQuery,
} from '@clockwise/clients/reports';
import type { User } from '@clockwise/clients/users';
import {
  ApiError,
  isoDateString,
  REPORT_SORT_FIELDS,
  TIMESHEET_STATUSES,
  type TimesheetStatus,
} from '@clockwise/shared';
import {
  DATE_RANGE_PRESET_LABELS,
  DateRangeFilter,
  type DateRangeValue,
  prettyDateRange,
  resolveDateRange,
} from '@clockwise/ui';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, Download, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { EntityMultiSelect } from '@/components/custom/entity-multi-select';
import {
  customerPickerConfig,
  departmentPickerConfig,
  userPickerConfig,
} from '@/components/custom/entity-pickers';
import { MultiSelect, type MultiSelectOption } from '@/components/custom/multi-select';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { UserRow } from '@/components/custom/user-row';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { reportsApi } from '@/lib/api';
import { dateRangePresetSchema } from '@/lib/date-range-search';
import { formatRate } from '@/lib/format-rate';
import { queryKeys } from '@/lib/query-keys';
import { cn, formatIsoDate } from '@/lib/utils';

const STATUS_LABELS: Record<TimesheetStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  failed: 'Failed',
};
const STATUS_OPTIONS: MultiSelectOption[] = TIMESHEET_STATUSES.map((s) => ({
  value: s,
  label: STATUS_LABELS[s],
}));

const SORTABLE_KEYS = new Set<string>(REPORT_SORT_FIELDS);
// Columns whose values can run long and should wrap rather than force the
// table onto one unreadable line: customer name, activity name, description.
const WRAPPING_COLUMN_KEYS = new Set<string>(['customerName', 'product', 'description']);
const PAGE_SIZE = 500;
const DEFAULT_SORT_FIELD: ReportSortField = 'date';

/** The five id-set filters, keyed by their URL search param. */
const ENTITY_FILTER_KEYS = [
  'customerIds',
  'employeeIds',
  'srControllerIds',
  'teamLeadIds',
  'departmentIds',
] as const;
type EntityFilterKey = (typeof ENTITY_FILTER_KEYS)[number];

const idList = z.array(z.string().uuid()).optional();

const search = z
  .object({
    preset: dateRangePresetSchema.optional(),
    from: isoDateString().optional(),
    to: isoDateString().optional(),
    status: z.array(z.enum(TIMESHEET_STATUSES)).optional(),
    customerIds: idList,
    employeeIds: idList,
    srControllerIds: idList,
    teamLeadIds: idList,
    departmentIds: idList,
    // Append a row per full-time employee per business day in the range with
    // no timesheet entries. Incompatible with customer/team-lead filters — the
    // normalize step below strips it whenever either is set.
    includeMissing: z.boolean().optional(),
    // 'results' = the report has been run; otherwise the filter screen shows.
    view: z.enum(['filters', 'results']).optional(),
    page: z.coerce.number().int().min(0).optional(),
    sortField: z.enum(REPORT_SORT_FIELDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
  })
  // includeMissing can't coexist with a customer or team-lead filter (the API
  // 400s on the combination). Strip it at the URL boundary so the invalid pair
  // can never enter route state — whether from a hand-edited/bookmarked URL or
  // a transient update. This is the single source of truth; the toggle's
  // disabled state below is just the visible reflection of it. Stripping
  // (not masking) also means a stale includeMissing can't silently resurrect
  // when the customer filter is later cleared.
  .transform((s) =>
    s.includeMissing && ((s.customerIds?.length ?? 0) > 0 || (s.teamLeadIds?.length ?? 0) > 0)
      ? { ...s, includeMissing: undefined }
      : s,
  );
type ReportSearch = z.infer<typeof search>;
type EntityFilters = Record<EntityFilterKey, string[]>;

/**
 * Single source of truth for the entity filters — drives the filter-screen
 * controls AND the results subtitle, so a filter can't be wired into one and
 * forgotten in the other. The three user-backed filters reuse one config.
 *
 * Dropdown page size rides along with the spread picker config (see
 * `entity-pickers`), so these filters list their catalog rather than a
 * 20-row slice of it without each having to say so.
 */
interface EntityFilterDef {
  key: EntityFilterKey;
  label: string;
  /** [singular, plural] for the results subtitle. */
  noun: readonly [string, string];
  render: (props: {
    id: string;
    selected: string[];
    onChange: (ids: string[]) => void;
  }) => React.ReactNode;
}

const ENTITY_FILTERS: readonly EntityFilterDef[] = [
  {
    key: 'customerIds',
    label: 'Customer',
    noun: ['customer', 'customers'],
    render: ({ id, selected, onChange }) => (
      <EntityMultiSelect<Customer>
        id={id}
        className="w-full"
        {...customerPickerConfig}
        selected={selected}
        onChange={onChange}
        allLabel="All customers"
        searchPlaceholder="Search customers…"
        popoverWidth="auto"
      />
    ),
  },
  {
    key: 'employeeIds',
    label: 'Employee',
    noun: ['employee', 'employees'],
    render: ({ id, selected, onChange }) => (
      <EntityMultiSelect<User>
        id={id}
        className="w-full"
        {...userPickerConfig}
        selected={selected}
        onChange={onChange}
        allLabel="All employees"
        searchPlaceholder="Search employees…"
        renderItem={(u, sel) => <UserRow user={u} isSelected={sel} />}
        popoverWidth="auto"
      />
    ),
  },
  {
    key: 'srControllerIds',
    label: 'Sr Controller',
    noun: ['sr controller', 'sr controllers'],
    render: ({ id, selected, onChange }) => (
      <EntityMultiSelect<User>
        id={id}
        className="w-full"
        {...userPickerConfig}
        selected={selected}
        onChange={onChange}
        allLabel="All sr controllers"
        searchPlaceholder="Search employees…"
        renderItem={(u, sel) => <UserRow user={u} isSelected={sel} />}
        popoverWidth="auto"
      />
    ),
  },
  {
    key: 'teamLeadIds',
    label: 'Team Lead',
    noun: ['team lead', 'team leads'],
    render: ({ id, selected, onChange }) => (
      <EntityMultiSelect<User>
        id={id}
        className="w-full"
        {...userPickerConfig}
        selected={selected}
        onChange={onChange}
        allLabel="All team leads"
        searchPlaceholder="Search employees…"
        renderItem={(u, sel) => <UserRow user={u} isSelected={sel} />}
        popoverWidth="auto"
      />
    ),
  },
  {
    key: 'departmentIds',
    label: 'Department',
    noun: ['department', 'departments'],
    render: ({ id, selected, onChange }) => (
      <EntityMultiSelect<Department>
        id={id}
        className="w-full"
        {...departmentPickerConfig}
        loadStrategy="preload"
        selected={selected}
        onChange={onChange}
        allLabel="All departments"
        searchPlaceholder="Search departments…"
      />
    ),
  },
];

export const Route = createFileRoute('/_authenticated/reports/timesheets')({
  validateSearch: search,
  component: TimesheetReportPage,
});

function TimesheetReportPage() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const rangeValue: DateRangeValue = {
    preset: s.preset ?? 'current_month',
    from: s.from,
    to: s.to,
  };
  const resolved = resolveDateRange(rangeValue);
  const statuses = s.status ?? [];
  const entityFilters: EntityFilters = {
    customerIds: s.customerIds ?? [],
    employeeIds: s.employeeIds ?? [],
    srControllerIds: s.srControllerIds ?? [],
    teamLeadIds: s.teamLeadIds ?? [],
    departmentIds: s.departmentIds ?? [],
  };

  // Missing-timesheet rows have no customer, so the toggle is incompatible
  // with customer/team-lead filters (the API rejects the combination).
  const missingDisabled =
    entityFilters.customerIds.length > 0 || entityFilters.teamLeadIds.length > 0;
  const includeMissing = (s.includeMissing ?? false) && !missingDisabled;

  const update = (next: Partial<ReportSearch>) =>
    navigate({ search: (prev: ReportSearch) => ({ ...prev, ...next }), replace: true });

  // A filter change resets to page 0 (only meaningful once viewing results).
  // Sort the ids so a different pick order yields the same URL + cache key.
  // Picking a customer / team lead strips includeMissing via the search
  // schema's normalize step, so the toggle visibly switches off rather than
  // going silently inert — no special-casing needed here.
  const setEntityFilter = (key: EntityFilterKey, ids: string[]) =>
    update({ [key]: ids.length > 0 ? [...ids].sort() : undefined, page: 0 });

  if (s.view !== 'results') {
    return (
      <FilterScreen
        rangeValue={rangeValue}
        canRun={resolved !== null}
        statuses={statuses}
        entityFilters={entityFilters}
        includeMissing={includeMissing}
        missingDisabled={missingDisabled}
        onRange={(next) => update({ preset: next.preset, from: next.from, to: next.to })}
        onStatuses={(next) => update({ status: next.length > 0 ? next : undefined })}
        onEntityFilter={setEntityFilter}
        onIncludeMissing={(on) => update({ includeMissing: on ? true : undefined, page: 0 })}
        onRun={() => update({ view: 'results', page: 0 })}
      />
    );
  }

  return (
    <ResultsScreen
      rangeValue={rangeValue}
      resolved={resolved}
      statuses={statuses}
      entityFilters={entityFilters}
      includeMissing={includeMissing}
      page={s.page ?? 0}
      sortField={s.sortField ?? DEFAULT_SORT_FIELD}
      sortDirection={s.sortDirection ?? 'desc'}
      onEditFilters={() => update({ view: 'filters', page: 0 })}
      update={update}
    />
  );
}

// ── Screen 1: pick filters, then run ────────────────────────────────────────
function FilterScreen(props: {
  rangeValue: DateRangeValue;
  canRun: boolean;
  statuses: TimesheetStatus[];
  entityFilters: EntityFilters;
  includeMissing: boolean;
  missingDisabled: boolean;
  onRange: (next: DateRangeValue) => void;
  onStatuses: (next: TimesheetStatus[]) => void;
  onEntityFilter: (key: EntityFilterKey, ids: string[]) => void;
  onIncludeMissing: (on: boolean) => void;
  onRun: () => void;
}) {
  const { entityFilters: f, onEntityFilter } = props;
  return (
    <PageShell>
      <PageHeader
        title="Timesheet report"
        subtitle="Choose a date range and filters, then run the report."
      />

      <div className="surface-card max-w-xl space-y-5 rounded-lg border p-5">
        <DateRangeFilter id="report-range" value={props.rangeValue} onChange={props.onRange} />

        <div className="grid gap-1.5">
          <Label htmlFor="report-status">Status</Label>
          <MultiSelect
            id="report-status"
            className="w-full"
            options={STATUS_OPTIONS}
            selected={props.statuses}
            allLabel="All statuses"
            onChange={(next) => props.onStatuses(next as TimesheetStatus[])}
          />
        </div>

        {ENTITY_FILTERS.map((def) => {
          const fieldId = `report-${def.key}`;
          return (
            <div key={def.key} className="grid gap-1.5">
              <Label htmlFor={fieldId}>{def.label}</Label>
              {def.render({
                id: fieldId,
                selected: f[def.key],
                onChange: (ids) => onEntityFilter(def.key, ids),
              })}
            </div>
          );
        })}

        <div className="flex items-start gap-3">
          <Switch
            id="report-include-missing"
            checked={props.includeMissing}
            disabled={props.missingDisabled}
            onCheckedChange={props.onIncludeMissing}
            className="mt-0.5"
          />
          <div className="grid gap-0.5">
            <Label
              htmlFor="report-include-missing"
              className={cn(props.missingDisabled && 'text-muted-foreground')}
            >
              Include full-time employees with missing timesheets
            </Label>
            <p className="text-xs text-muted-foreground">
              {props.missingDisabled
                ? 'Not available with a customer or team-lead filter.'
                : props.statuses.length > 0
                  ? 'Adds a row per full-time employee for each business day in the range with no timesheet entries. These rows are listed regardless of the status filter.'
                  : 'Adds a row per full-time employee for each business day in the range with no timesheet entries.'}
            </p>
          </div>
        </div>

        <Button onClick={props.onRun} disabled={!props.canRun}>
          Run report
        </Button>
      </div>
    </PageShell>
  );
}

// ── Screen 2: results + export ──────────────────────────────────────────────
function ResultsScreen(props: {
  rangeValue: DateRangeValue;
  resolved: { from: string; to: string } | null;
  statuses: TimesheetStatus[];
  entityFilters: EntityFilters;
  includeMissing: boolean;
  page: number;
  sortField: ReportSortField;
  sortDirection: 'asc' | 'desc';
  onEditFilters: () => void;
  update: (next: Partial<ReportSearch>) => void;
}) {
  const {
    rangeValue,
    resolved,
    statuses,
    entityFilters,
    includeMissing,
    page,
    sortField,
    sortDirection,
    update,
  } = props;
  const rangeValid = resolved !== null;
  const fromDate = resolved?.from ?? '';
  const toDate = resolved?.to ?? '';

  // Only send non-empty id sets so the query key (and the API request) stay
  // free of empty params — keeps cache hits stable.
  const entityQuery = entityFiltersToQuery(entityFilters);

  const listQuery: TimesheetReportQuery = {
    fromDate,
    toDate,
    status: statuses.length > 0 ? statuses : undefined,
    ...entityQuery,
    includeMissing: includeMissing || undefined,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const columnsQuery = useQuery({
    queryKey: queryKeys.reports.timesheetColumns(),
    queryFn: () => reportsApi.timesheetColumns(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const columns = columnsQuery.data?.data ?? [];

  const reportQuery = useQuery({
    queryKey: queryKeys.reports.timesheet(listQuery),
    queryFn: () => reportsApi.timesheetReport(listQuery),
    enabled: rangeValid,
    placeholderData: keepPreviousData,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      reportsApi.exportTimesheetReport({
        fromDate,
        toDate,
        status: statuses.length > 0 ? statuses : undefined,
        ...entityQuery,
        includeMissing: includeMissing || undefined,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'timesheet-report.xlsx');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Export failed');
    },
  });

  const rows = reportQuery.data?.data ?? [];
  const total = reportQuery.data?.meta.total ?? 0;
  const totals = reportQuery.data?.meta.totals;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + rows.length);

  const toggleSort = (key: string) => {
    if (!SORTABLE_KEYS.has(key)) return;
    const field = key as ReportSortField;
    if (sortField === field) {
      update({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc', page: 0 });
    } else {
      update({ sortField: field, sortDirection: 'asc', page: 0 });
    }
  };

  const statusSummary =
    statuses.length === 0 ? 'All statuses' : statuses.map((st) => STATUS_LABELS[st]).join(', ');
  const filterSummary = entityFilterSummary(entityFilters);
  const subtitle = [
    DATE_RANGE_PRESET_LABELS[rangeValue.preset],
    prettyDateRange(resolved),
    statusSummary,
    ...(filterSummary ? [filterSummary] : []),
    ...(includeMissing ? ['Incl. missing timesheets'] : []),
  ].join(' · ');

  return (
    <PageShell>
      <PageHeader title="Timesheet report" subtitle={subtitle}>
        <Button variant="outline" onClick={props.onEditFilters}>
          <SlidersHorizontal />
          Edit filters
        </Button>
        <Button
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending || !rangeValid}
        >
          <Download />
          {exportMutation.isPending ? 'Exporting…' : 'Export'}
        </Button>
      </PageHeader>

      <div className="flex items-center text-xs text-muted-foreground">
        <span className="ml-auto">{reportQuery.isFetching ? 'Loading…' : `${total} total`}</span>
      </div>

      <StickyTableScroll>
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              {columns.map((col) => {
                const sortable = SORTABLE_KEYS.has(col.key);
                const active = sortField === col.key;
                return (
                  <TableHead
                    key={col.key}
                    className={cn('whitespace-nowrap', col.type === 'number' && 'text-right')}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="inline-flex select-none items-center gap-1 hover:text-foreground"
                        onClick={() => toggleSort(col.key)}
                      >
                        {col.header}
                        {active && sortDirection === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : active && sortDirection === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ArrowUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {reportQuery.isLoading || columnsQuery.isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {(columns.length > 0 ? columns : Array.from({ length: 6 })).map((_c, j) => (
                    <TableCell key={`skel-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : reportQuery.isError ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, columns.length)}
                  className="py-10 text-center text-destructive"
                >
                  {reportQuery.error instanceof ApiError
                    ? `${reportQuery.error.message}${reportQuery.error.traceId ? ` (trace ${reportQuery.error.traceId})` : ''}`
                    : 'Failed to load the report.'}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, columns.length)}
                  className="py-10 text-center text-muted-foreground"
                >
                  No timesheet entries match this date range and status.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow key={`${row.date}-${i}`}>
                  {columns.map((col) => {
                    const raw = row[col.key];
                    return (
                      <TableCell
                        key={col.key}
                        className={cn(
                          // Top-align every cell so short values line up with the
                          // first line of any wrapped long-text cell in the same
                          // row (rather than centering against the tall cell).
                          'align-top',
                          // Free-text/long-name columns can run long: constrain
                          // their width and wrap so they never blow the table out
                          // to one unreadable line. Every other column stays
                          // single-line.
                          WRAPPING_COLUMN_KEYS.has(col.key)
                            ? 'max-w-xs whitespace-normal break-words'
                            : 'whitespace-nowrap',
                          col.type === 'number' && 'text-right tabular-nums',
                        )}
                      >
                        {raw ? (
                          formatReportCell(col, raw)
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
          {/* Grand totals across the whole filtered report (not just this
              page). Gated on the CURRENT page having rows — not just
              `total > 0` — so an out-of-range/bookmarked page renders the
              "no entries" empty state alone, never an empty body with a
              populated Total beneath it. Also hidden while loading or errored. */}
          {rows.length > 0 && totals && !reportQuery.isError && columns.length > 0 && (
            <TableFooter>
              <TableRow>
                {columns.map((col, i) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'font-semibold',
                      col.type === 'number' && 'text-right tabular-nums',
                    )}
                  >
                    {i === 0
                      ? 'Total'
                      : col.key === 'hours'
                        ? (totals.hours ?? '0')
                        : col.key === 'amount'
                          ? formatRate(totals.amount ?? '0')
                          : null}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => update({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || reportQuery.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => update({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || reportQuery.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>
    </PageShell>
  );
}

/** Display a preview cell. The server already projects every cell to a
 *  string; we reshape `'date'` columns into the app-wide date format so the
 *  on-screen table matches the typed xlsx export, and render the monetary
 *  `amount` column as USD currency. Other numbers render as-is (right-aligned
 *  via the cell className). */
function formatReportCell(col: ReportColumnMeta, raw: string): string {
  if (col.type === 'date') return formatIsoDate(raw);
  if (col.key === 'amount') return formatRate(raw);
  return raw;
}

/** Drop empty id sets (no predicate) and sort the rest so the query key +
 *  request are order-independent and free of empty params. */
function entityFiltersToQuery(f: EntityFilters): Pick<TimesheetReportQuery, EntityFilterKey> {
  const out = {} as Pick<TimesheetReportQuery, EntityFilterKey>;
  for (const key of ENTITY_FILTER_KEYS) {
    const ids = f[key];
    out[key] = ids.length > 0 ? [...ids].sort() : undefined;
  }
  return out;
}

/** "2 customers, 1 employee" — the active entity filters, for the results
 *  subtitle. Nouns come from ENTITY_FILTERS so they can't drift. Null when
 *  none are set. */
function entityFilterSummary(f: EntityFilters): string | null {
  const parts = ENTITY_FILTERS.flatMap((def) => {
    const n = f[def.key].length;
    if (n === 0) return [];
    const [one, many] = def.noun;
    return [`${n} ${n === 1 ? one : many}`];
  });
  return parts.length > 0 ? parts.join(', ') : null;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
